import { useState, type ReactNode } from 'react';
import { t } from '../../i18n';
import { monthName } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ShareFile } from '../../components/ShareFile';
import { Preview } from '../belege/BelegSheet';
import { MailToEmployee } from './MailToEmployee';
import type { EmployeeDetail } from './types';

// „Verdienstabrechnung herunterladen“ in der Personalakte (Sam 12.10.): Monat wählen, die App nimmt aus den
// Lohnabrechnungen der Kanzlei die Seiten dieses Mitarbeiters (server/src/modules/lohn/verdienst.ts).

interface Monat {
  year: number;
  month: number;
}

export function VerdienstView({ e, onBack }: { e: EmployeeDetail; onBack(): void }) {
  const res = useApi<{ months: Monat[] }>(`/api/app/employee/${e.id}/verdienst`, { live: ['lohn'] });
  const months = res.data?.months;
  const [pick, setPick] = useState<string | null>(null);
  if (!months) return res.error ? <p className="meta">{res.error}</p> : <div className="spinner" aria-label={t('Lädt')} />;
  const back = (
    <button className="btn2" type="button" onClick={onBack}>
      {t('Zurück')}
    </button>
  );
  if (!months.length) {
    return (
      <div className="kdg">
        <p className="meta">
          {t('Noch keine Lohnabrechnungen für {name}. Ihre Kanzlei lädt sie nach jeder Abrechnung hoch.', { name: e.name })}
        </p>
        <div className="row">{back}</div>
      </div>
    );
  }
  const key = (m: Monat) => `${m.year}-${m.month}`;
  const chosen = months.find((m) => key(m) === pick) ?? months[0]!;
  return (
    <div className="kdg">
      <div className="form maform">
        <div className="fld">
          <label htmlFor="vd-monat">
            <span>{t('Monat')}</span>
            <select id="vd-monat" value={key(chosen)} onChange={(ev) => setPick(ev.target.value)}>
              {months.map((m) => (
                <option key={key(m)} value={key(m)}>
                  {monthName(m.month)} {m.year}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <Abrechnung key={key(chosen)} e={e} m={chosen} back={back} />
    </div>
  );
}

function Abrechnung({ e, m, back }: { e: EmployeeDetail; m: Monat; back: ReactNode }) {
  const url = `/api/app/employee/${e.id}/verdienst/${m.year}/${m.month}`;
  // vorher prüfen, ob es Seiten gibt: sonst zeigte die Vorschau nur „nicht verfügbar“
  const check = useApi<{ seiten: number }>(`${url}/seiten`);
  if (!check.data) {
    return check.error ? (
      <>
        <p className="kdg-warn">{check.error}</p>
        <div className="row">{back}</div>
      </>
    ) : (
      <div className="spinner" aria-label={t('Lädt')} />
    );
  }
  const name = `${t('Verdienstabrechnung')} ${e.name} ${monthName(m.month)} ${m.year}.pdf`;
  return (
    <>
      <Preview url={url} mime="application/pdf" name={name} />
      <div className="row">
        <a className="btn2 dark" href={`${url}?download=1`} download={name}>
          {t('PDF herunterladen')}
        </a>
        <ShareFile url={`${url}?download=1`} name={name} />
        <MailToEmployee
          e={e}
          url={`${url}/mail`}
          text={t(
            '{name} bekommt an {email} einen Link zur Verdienstabrechnung {month}. Geöffnet wird sie mit dem Geburtsdatum, der Link gilt 14 Tage.',
            { month: `${monthName(m.month)} ${m.year}`, email: e.email ?? '', name: e.name },
          )}
        />
        {back}
      </div>
    </>
  );
}
